import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { getSignedMediaUrl } from '@/lib/utils';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import CustomVideoPlayer from './CustomVideoPlayer';
import { clubsService } from '@/features/clubs/services/clubsService';
import type { 
  GalleryItemResponse, 
  GalleryRelatedEntitiesResponse, 
  GalleryRelatedEntityItem 
} from '@/features/clubs/types/types';

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentItem: GalleryItemResponse | null;
  currentIndex: number;
  allItems: GalleryItemResponse[];
  clubHandle: string;
  onNavigate: (index: number) => void;
}

interface EntityCardProps {
  entity: GalleryRelatedEntityItem;
  clubHandle: string;
  variant: 'main' | 'related';
}

const EntityCard: React.FC<EntityCardProps> = ({ entity, clubHandle, variant }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    if (entity.entity_type === 'event_entity') {
      navigate(`/venues/${clubHandle}/events/${entity.id}`);
    } else if (entity.entity_type === 'artist_entity') {
      navigate(`/venues/${clubHandle}/artist/${entity.id}`);
    } else if (entity.entity_type === 'booking_category_entity') {
      navigate(`/venues/${clubHandle}/booking/${entity.id}`);
    } else if (entity.entity_type === 'business_entity') {
      navigate(`/venues/${clubHandle}`);
    }
  };

  const imageUrl = entity.picture_id 
    ? getSignedMediaUrl(entity.picture_id, 'image').url 
    : '';

  const isArtist = entity.entity_type === 'artist_entity';
  const isEvent = entity.entity_type === 'event_entity';

  // Event Portrait Card (Image as background)
  if (isEvent) {
    // Main Event Entity - 16:9, Larger Text
    if (variant === 'main') {
      return (
        <motion.div
            className="relative w-full aspect-video rounded-2xl overflow-hidden cursor-pointer group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClick}
        >
            {/* Background Image */}
            <div className="absolute inset-0 bg-gray-800">
            {imageUrl && !imageError ? (
                <img
                src={imageUrl}
                alt={entity.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-indigo-900">
                <span className="text-4xl font-bold text-white/20 uppercase">{entity.name.charAt(0)}</span>
                </div>
            )}
            </div>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />

            {/* Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
            <p className="text-xs text-blue-400 font-bold mb-1 uppercase tracking-wider">Event</p>
            <h4 className="text-white font-bold text-2xl leading-tight mb-2">
                {entity.name}
            </h4>
            {entity.description && (
                <p className="text-gray-200 text-sm line-clamp-2 opacity-90">
                {entity.description}
                </p>
            )}
            </div>
        </motion.div>
      );
    }

    // Related Event - Portrait 2:3, Smaller Text
    return (
      <motion.div
        className="relative w-full aspect-[2/3] rounded-2xl overflow-hidden cursor-pointer group"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
      >
        {/* Background Image */}
        <div className="absolute inset-0 bg-gray-800">
           {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={entity.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-indigo-900">
               <span className="text-4xl font-bold text-white/20 uppercase">{entity.name.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Gradient Overlay - Matches FavouritesSection */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Content Overlay - Matches FavouritesSection typography */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h4 className="text-white font-medium text-sm truncate mb-0.5">
            {entity.name}
          </h4>
          {entity.description && (
            <p className="text-gray-300 text-xs truncate opacity-90">
              {entity.description}
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  // Artist / Other Main Entity (Horizontal with large image/circle)
  if (variant === 'main') {
    return (
      <motion.div
        className={`flex items-center gap-5 p-5 rounded-2xl cursor-pointer group bg-white/5 hover:bg-white/10 transition-colors duration-200 border border-white/5 hover:border-white/10`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={handleClick}
      >
        {/* Image - Circular for Artist, Rounded Rect for others */}
        <div className={`relative flex-shrink-0 ${isArtist ? 'w-24 h-24 rounded-full' : 'w-24 h-16 rounded-lg'} overflow-hidden bg-gray-800 shadow-lg`}>
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={entity.name}
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
               {/* Use first letter as placeholder */}
               <span className="text-xl font-medium uppercase">{entity.name.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold text-lg truncate mb-1">
            {entity.name}
          </h4>
          {entity.description && (
            <p className="text-gray-200 text-sm line-clamp-2 opacity-90">
              {entity.description}
            </p>
          )}
        </div>

        <ChevronRightIcon className="w-6 h-6 text-gray-500 group-hover:text-white transition-colors" />
      </motion.div>
    );
  }

  // Fallback / Other Related content card (Compact List Item)
  return (
    <motion.div
      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer group bg-white/5 hover:bg-white/10 transition-colors duration-200"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
    >
      {/* Image */}
      <div className={`relative flex-shrink-0 ${isArtist ? 'w-12 h-12 rounded-full' : 'w-16 h-12 rounded-md'} overflow-hidden bg-gray-800`}>
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={entity.name}
            className="w-full h-full object-cover"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            style={{ opacity: imageLoaded ? 1 : 0 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            <span className="text-xs font-medium uppercase">{entity.name.charAt(0)}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium text-sm truncate">
          {entity.name}
        </h4>
        {isArtist && entity.description && (
           <p className="text-gray-500 text-xs truncate mt-0.5">{entity.description}</p>
        )}
      </div>

      <ChevronRightIcon className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
    </motion.div>
  );
};

const GalleryModal: React.FC<GalleryModalProps> = ({
  isOpen,
  onClose,
  currentItem,
  currentIndex,
  allItems,
  clubHandle,
  onNavigate
}) => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [relatedEntities, setRelatedEntities] = useState<GalleryRelatedEntitiesResponse | null>(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Determine media type from content_type field
  const isVideo = currentItem?.content_type?.startsWith('video');
  const mediaUrl = currentItem ? getSignedMediaUrl(currentItem.media_id, isVideo ? 'video' : 'image').url : '';

  // Fetch related entities when item changes
  useEffect(() => {
    if (currentItem && isOpen) {
      setLoadingRelated(true);
      clubsService.getGalleryRelatedEntities(currentItem.id)
        .then(data => {
          setRelatedEntities(data);
        })
        .finally(() => {
          setLoadingRelated(false);
        });
    } else {
      setRelatedEntities(null);
    }
  }, [currentItem?.id, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setSlideDirection('left');
            onNavigate(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          if (currentIndex < allItems.length - 1) {
            setSlideDirection('right');
            onNavigate(currentIndex + 1);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, allItems.length, onClose, onNavigate]);

  // Reset media state when item changes
  useEffect(() => {
    setMediaLoaded(false);
    setMediaError(false);
  }, [currentItem]);

  // Auto-play video when modal opens
  useEffect(() => {
    if (isOpen && isVideo && videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  }, [isOpen, isVideo]);

  if (!isOpen || !currentItem) return null;

  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < allItems.length - 1;

  // Split related content into categories
  const relatedArtists = relatedEntities?.related_content.filter(e => e.entity_type === 'artist_entity') || [];
  const relatedEvents = relatedEntities?.related_content.filter(e => e.entity_type === 'event_entity') || [];
  
  const hasMainEntity = !!relatedEntities?.main_entity;
  const hasRelatedArtists = relatedArtists.length > 0;
  const hasRelatedEvents = relatedEvents.length > 0;
  const hasAnyRelatedContent = hasMainEntity || hasRelatedArtists || hasRelatedEvents;
  const showSidebar = hasAnyRelatedContent || loadingRelated;

  const slideVariants = {
    enter: (direction: 'left' | 'right') => ({
      x: direction === 'right' ? 50 : -50,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: 'left' | 'right') => ({
      x: direction === 'right' ? -50 : 50,
      opacity: 0,
      scale: 0.95
    })
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        onClick={onClose}
      >
        <motion.div
           // Use grid layout for 2 columns. Max width constrained. Full height handling.
          className="relative w-full max-w-[90vw] h-[90vh] bg-black/40 rounded-2xl overflow-hidden border border-white/10 flex"
          initial={{ scale: 0.9, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 30 }}
          transition={{ 
            duration: 0.5, 
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - Absolute over everything */}
          <motion.button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 bg-black/60 backdrop-blur-md rounded-full p-2 border border-white/20 hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <XIcon className="w-5 h-5 text-white" />
          </motion.button>

          {/* LEFT COLUMN: Media Content */}
          <div className={`${showSidebar ? 'w-[70%]' : 'w-full'} h-full relative flex items-center justify-center bg-black/20`}>
            
            {/* Navigation Arrows (Floating in Media Area) */}
            {canGoBack && (
              <motion.button
                onClick={(e) => {
                   e.stopPropagation();
                   setSlideDirection('left');
                   onNavigate(currentIndex - 1);
                }}
                className="absolute left-4 z-10 bg-black/40 backdrop-blur-sm rounded-full p-3 hover:bg-white/10 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeftIcon className="w-6 h-6 text-white" />
              </motion.button>
            )}

            {canGoForward && (
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideDirection('right');
                  onNavigate(currentIndex + 1);
                }}
                className="absolute right-4 z-10 bg-black/40 backdrop-blur-sm rounded-full p-3 hover:bg-white/10 transition-colors"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRightIcon className="w-6 h-6 text-white" />
              </motion.button>
            )}

            {/* Image Counter */}
            <div className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-md rounded-full px-3 py-1 text-xs text-white/80 font-medium border border-white/10">
              {currentIndex + 1} / {allItems.length}
            </div>

            {/* Media Player/Image */}
            <div className="w-full h-full p-4 flex items-center justify-center">
               <AnimatePresence mode="wait" custom={slideDirection}>
                  {mediaError ? (
                    <div className="text-center text-gray-500">
                      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <p>Media unavailable</p>
                    </div>
                  ) : (
                    <motion.div
                       key={`${currentItem.id}-${currentIndex}`}
                       custom={slideDirection}
                       variants={slideVariants}
                       initial="enter"
                       animate="center"
                       exit="exit"
                       transition={{ duration: 0.3, ease: "easeOut" }}
                       className="relative w-full h-full flex items-center justify-center"
                    >
                      {isVideo ? (
                        <CustomVideoPlayer
                          src={mediaUrl}
                          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                          autoPlay={true}
                          muted={false}
                          loop={true}
                          playsInline={true}
                          onLoad={() => setMediaLoaded(true)}
                          onError={() => setMediaError(true)}
                        />
                      ) : (
                        <img
                          src={mediaUrl}
                          alt="Gallery item"
                          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                          onLoad={() => setMediaLoaded(true)}
                          onError={() => setMediaError(true)}
                          style={{ opacity: mediaLoaded ? 1 : 0 }}
                        />
                      )}
                      
                      {/* Loading Spinner */}
                       {!mediaLoaded && !mediaError && (
                         <div className="absolute inset-0 flex items-center justify-center">
                           <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                         </div>
                       )}
                    </motion.div>
                  )}
               </AnimatePresence>
            </div>
          </div>

          {/* RIGHT COLUMN: Sidebar (Related Content) */}
          {showSidebar && (
            <div className="w-[30%] h-full border-l border-white/10 bg-black/40 flex flex-col overflow-hidden">
               <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                  {loadingRelated ? (
                     <div className="flex justify-center py-12">
                       <div className="w-6 h-6 border-2 border-white/20 border-t-blue-500 rounded-full animate-spin" />
                     </div>
                  ) : (
                    <div className="space-y-8 pb-8">
                       {/* Main Entity Section */}
                       {relatedEntities?.main_entity && (
                          <div className="space-y-3">
                             {/* No heading for main entity as requested, just the card */}
                             <EntityCard 
                               entity={relatedEntities.main_entity} 
                               clubHandle={clubHandle}
                               variant="main"
                             />
                          </div>
                       )}

                       {/* Related Events Section */}
                       {hasRelatedEvents && (
                          <div className="min-w-0">
                             <h3 className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">Related Events</h3>
                             <div className="flex gap-4 overflow-x-auto custom-scrollbar pb-4 -mx-1 px-1">
                                {relatedEvents.map((entity, index) => (
                                   <div key={`event-${entity.id}-${index}`} className="flex-shrink-0 w-[200px]">
                                     <EntityCard 
                                        entity={entity} 
                                        clubHandle={clubHandle}
                                        variant="related" // Will render as portrait card because it's an event
                                     />
                                   </div>
                                ))}
                             </div>
                          </div>
                       )}

                       {/* Related Artists Section */}
                       {hasRelatedArtists && (
                          <div>
                             <h3 className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">Related Artists</h3>
                             <div className="space-y-2">
                                {relatedArtists.map((entity, index) => (
                                   <EntityCard 
                                      key={`artist-${entity.id}-${index}`}
                                      entity={entity} 
                                      clubHandle={clubHandle}
                                      variant="related" // Will render as compact list item
                                   />
                                ))}
                             </div>
                          </div>
                       )}
                    </div>
                  )}
               </div>
            </div>
          )}

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GalleryModal;
